'use client';

import { Fragment, PropsWithChildren, useState } from 'react';
import Image from 'next/image';
import { Dialog, Transition } from '@headlessui/react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

import { liteClient } from 'algoliasearch/lite';
import {
  useInstantSearch,
  InstantSearch,
  SearchBox,
  Hits,
  Highlight,
  Snippet,
  HitsProps,
} from 'react-instantsearch-hooks-web';
import type { BaseHit } from 'instantsearch.js/es/types';

import type { SearchRecord } from '../../bin/algoliaSearch';
import { InsightsMiddleware } from './InsightsMiddleware';

const searchClient = liteClient(
  process.env.NEXT_PUBLIC_ALGOLIA_APP_ID ?? '',
  process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_ONLY_API_KEY ?? '',
);

// Algolia search-insights seems to expect headers to be defined. baseQueryParameters contains the auth credentials. Might be an issue with using algoliasearch 5.0.0-alpha.36. The search-insights might not have been updated yet.
// @ts-ignore
searchClient.transporter.headers = searchClient.transporter.baseHeaders;
// @ts-ignore
searchClient.transporter.queryParameters =
  searchClient.transporter.baseQueryParameters;

const typeToBaseUri = (type: string) => {
  switch (type) {
    case 'Agent':
      return 'agents/';
    case 'Article':
      return 'article/';
    case 'City':
      return 'cities/';
    case 'Page':
      return '';
    default:
      return 'unknown';
  }
};

const Hit: HitsProps<BaseHit & SearchRecord>['hitComponent'] = ({
  hit,
  sendEvent,
}) => {
  return (
    <a
      href={`/${typeToBaseUri(hit.type)}${hit.slug}`}
      className="group flex flex-col sm:flex-row justify-between items-start my-2 border border-gray-300 hover:bg-gray-50 rounded-lg p-4"
      onClick={() => {
        sendEvent('click', hit, 'Navigate to result');
      }}
    >
      <div className="">
        <p className="text-gray-400">{hit.type}</p>
        <h2 className="group-hover:text-gray-700 font-semibold">
          <Highlight
            attribute="title"
            hit={hit}
            classNames={{ highlighted: 'bg-yellow-300' }}
          />
        </h2>
        <p className="text-sm">
          <Snippet
            attribute="description"
            hit={hit}
            classNames={{ highlighted: 'bg-yellow-300' }}
          />
        </p>
        <p className="mt-2">
          <Snippet
            attribute="content"
            hit={hit}
            classNames={{ highlighted: 'bg-yellow-300' }}
          />
        </p>
      </div>
      {hit.imageUri && (
        <Image
          src={hit.imageUri ?? ''}
          alt={hit.title}
          width={150}
          height={150}
          className="max-w-[150px] max-h-[150px] object-contain self-center mt-4 :sm:mt-0 sm:ml-4"
        />
      )}
    </a>
  );
};

const EmptyQueryBoundary: React.FC<PropsWithChildren> = ({ children }) => {
  const { indexUiState } = useInstantSearch();

  if (!indexUiState.query) {
    return null;
  }

  return <>{children}</>;
};

export const Search = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button onClick={() => setOpen(true)} aria-label="Search">
        <MagnifyingGlassIcon className="w-6 h-6" />
      </button>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-20" onClose={setOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white p-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl sm:p-6">
                  <InstantSearch
                    searchClient={searchClient}
                    indexName={process.env.NEXT_PUBLIC_ALGOLIA_INDEX_NAME}
                  >
                    <InsightsMiddleware />
                    <SearchBox
                      autoFocus
                      placeholder="Search"
                      classNames={{
                        root: 'border border-gray-300 rounded-lg focus-within:border-gray-500 bg-gray-100',
                        form: 'flex items-center',
                        input: 'w-full m-1 p-4 outline-none bg-transparent',
                        submitIcon: 'w-6 h-6 m-4',
                        resetIcon: 'w-3 h-3 m-4 ml-0',
                      }}
                    />
                    <EmptyQueryBoundary>
                      <Hits hitComponent={Hit} />
                    </EmptyQueryBoundary>
                  </InstantSearch>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};
